import React, { useCallback, useContext, useMemo, useState } from 'react'
import { useSnapshot } from 'valtio'
import { DATA_CLICK_AWAY_ID, DomNodes } from '@app/constants'
import {
  getChildrenWithInjectedProps,
  transformChoicesConstraints,
  useAssessmentItemResponseScoring,
  useFindNodes,
  useOutsideClick,
  useSelectableChoice,
  percentageFormatter,
  useKeyboardNavigation,
} from '@app/helpers'
import { CurrentTestState } from '@app/storage'
import { Cardinality, InteractionType } from '@app/types'
import { Box, Stack, styled } from '@mui/material'
import { ConstraintsAlertContainer } from '@app/components/ui/containers/ConstraintsAlertContainer'
import { SimpleChoiceComponent } from '@app/components/interactions/SimpleChoices'
import { AccessibilityTestMenuContext } from '@app/components/ui/containers/AccessibilityTestMenuContainer'
import { QuestionContextMenuContext } from '@app/components/common/QuestionContextMenuContext'

interface ChoiceInteractionProps {
  itemId: string
  componentNode: DomNodes
  responseidentifier?: string
  shuffle?: string
  maxchoices?: string
  minchoices?: string
  startLetter?: string
  orientation?: string
  children?: JSX.Element[]
  accessibilityAttr?: { [key: string]: string }
}

export const ChoiceInteractionComponent: React.FC<ChoiceInteractionProps> = props => {
  const { currentItemResponse } = useSnapshot(CurrentTestState)
  const {
    children,
    maxchoices,
    minchoices,
    startLetter,
    responseidentifier: responseDeclarationId,
    accessibilityAttr,
  } = props

  const {
    strikethroughAnswers = [],
    toggleStrikethrough = (answerId: string) => () => {},
    strikethroughEnabled = false,
  } = useContext(QuestionContextMenuContext)

  const {
    answerMaskingControl: { answerMaskingEnabled, answerIdsWithMaskByCurrentItem, toggleMaskOnAnswer },
  } = useContext(AccessibilityTestMenuContext)

  const [minChoices, maxChoices] = transformChoicesConstraints(minchoices, maxchoices)

  const [focusedChoice, setFocusedChoice] = useState<string>(null)

  const clickOutsideCallback = useCallback(() => setFocusedChoice(null), [])

  useOutsideClick(`[${DATA_CLICK_AWAY_ID}]`, clickOutsideCallback)

  const {
    selected: selectedChoices,
    onSelect,
    responseDeclaration,
    isValid,
    disabled,
  } = useSelectableChoice(responseDeclarationId, InteractionType.choice, { minChoices, maxChoices })
  const { correctResponse, groupMode, groupStatistics, isPreviewMode, isQuestionPreviewMode } =
    useAssessmentItemResponseScoring(currentItemResponse?.itemId, responseDeclarationId)

  const allowMultiple = responseDeclaration?.cardinality === Cardinality.multiple
  // a11y: multi (checkbox/group) unless the item is capped at exactly one — mirrors
  // useSelectableChoice, which forces single only at maxChoices === 1 (0 = unlimited).
  const availableMultipleChoices = allowMultiple && maxChoices !== 1
  const withLetterIndex = !allowMultiple || !!startLetter

  const childrenWithProps = getChildrenWithInjectedProps(
    DomNodes.simpleChoice,
    children,
    {
      minChoices,
      maxChoices,
      responseDeclaration,
      onSelect,
      selectedChoices: selectedChoices || [],
      isValid,
      disabled,
    },
    { withLetterIndex, startLetter },
  )

  const [[prompt], simpleChoice] = useFindNodes(childrenWithProps as JSX.Element[], [
    DomNodes.prompt,
    DomNodes.simpleChoice,
  ])

  const disabledAnswersIndexes = useMemo(() => {
    const disabledChoices = Array.from(
      new Set([
        ...(answerMaskingEnabled && !!answerIdsWithMaskByCurrentItem?.length
          ? answerIdsWithMaskByCurrentItem
          : []),
      ]),
    )

    return simpleChoice.reduce(
      (res, item, index) => (disabledChoices.includes(item.props.identifier) ? [...res, index] : res),
      [],
    )
  }, [
    simpleChoice,
    answerIdsWithMaskByCurrentItem,
    answerMaskingEnabled,
  ])

  const { onQuestionFocus, setBubblesRef, arrowKeysNavigation } = useKeyboardNavigation({
    navigationItems: simpleChoice.map(item => item.props.identifier),
    selectedItemIndex: 0,
    direction: 'both',
    disabledElementsIndexes: disabledAnswersIndexes,
  })

  // Prompt ID is used for aria-labelledby and aria-label
  const promptId = useMemo(() => {
    if (!props.itemId || !responseDeclarationId) return null
    return `${props.itemId}-${responseDeclarationId}-prompt`
  }, [props.itemId, responseDeclarationId])

  // For accessibility, we need to add an id to the prompt
  const promptWithId = useMemo(() => {
    if (!promptId) return prompt
    return React.isValidElement(prompt) ? React.cloneElement(prompt, { id: promptId } as any) : prompt
  }, [prompt, promptId])

  const padLength = Math.max(...Object.values(groupStatistics)) === 1 ? 4 : 3

  const feedbackPercentageFormatter = useCallback(
    (percentage: number) => percentageFormatter.format(percentage).padEnd(padLength, ' '),
    [padLength],
  )

  return (
    <>
      {currentItemResponse && responseDeclaration && (
        <Wrapper {...accessibilityAttr}>
          {promptWithId}
          {availableMultipleChoices && (
            <ConstraintsAlertContainer
              minChoices={minChoices}
              maxChoices={maxChoices}
              selectedCount={selectedChoices?.length}
            />
          )}
          <CheckboxesWrapper
            // a11y: group only when >1 choice is allowed (maxChoices > 1), else radiogroup — matches the choice role.
            role={availableMultipleChoices ? 'group' : 'radiogroup'}
            {...(promptId ? { 'aria-labelledby': promptId } : { 'aria-label': 'List of answers' })}
          >
            {simpleChoice.map(({ props, key }, index) => (
              <SimpleChoiceComponent
                ref={setBubblesRef(index)}
                setFocusedChoice={() => {
                  onQuestionFocus(index)
                  setFocusedChoice(props.identifier)
                }}
                isFocused={props.identifier === focusedChoice}
                onChoiceFocus={() => onQuestionFocus(index)}
                posInSet={index + 1}
                isCorrect={correctResponse?.includes(props.identifier)}
                groupMode={groupMode}
                isPreviewMode={isPreviewMode}
                masking={{
                  isEnabled: answerMaskingEnabled,
                  isMasked: !!answerIdsWithMaskByCurrentItem?.includes(props.identifier),
                  toggle: () => toggleMaskOnAnswer?.(props.identifier),
                }}
                strikethrough={{
                  isEnabled: strikethroughEnabled,
                  isStrikethrough: strikethroughAnswers.includes(props.identifier),
                  toggle: toggleStrikethrough(props.identifier),
                }}
                feedbackPercentage={groupStatistics[props.identifier] ?? 0}
                feedbackFormatter={feedbackPercentageFormatter}
                availableMultipleChoices={availableMultipleChoices}
                arrowKeysNavigation={arrowKeysNavigation}
                {...props}
                key={key}
              >
                {props.children}
              </SimpleChoiceComponent>
            ))}
            {groupMode && !isQuestionPreviewMode && !isPreviewMode && (
              <SimpleChoiceComponent
                itemId={props.itemId}
                blankBubble
                groupMode
                isPreviewMode={isPreviewMode}
                feedbackPercentage={groupStatistics['none']}
                feedbackFormatter={feedbackPercentageFormatter}
              >
                <>No Answer Selected</>
              </SimpleChoiceComponent>
            )}
          </CheckboxesWrapper>
        </Wrapper>
      )}
    </>
  )
}

const Wrapper = styled(Box)(
  () => `
  & hr {
    display: none;
  }
`,
)

const CheckboxesWrapper = styled(Stack)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  gap: theme.spacing(1),
}))
